import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  AuthShell,
  AUTH_FIELD_CLASS,
  AUTH_FOOTER_LINK_CLASS,
  AUTH_FOOTNOTE_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '@/components/auth/AuthShell';

function getPasswordStrength(pw: string): { score: number; label: string; bar: string; text: string } {
  if (!pw) return { score: 0, label: '', bar: 'bg-transparent', text: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  // Colour palette aligned with the cross-surface RAG split — focus-blue is
  // reserved for the focus role, so strong sits on emerald and warn states use
  // amber/red against the dark backdrop. The word beside the bar says it too.
  if (score <= 2) return { score: 25, label: 'Weak', bar: 'bg-red-400', text: 'text-red-400' };
  if (score <= 3) return { score: 50, label: 'Fair', bar: 'bg-amber-400', text: 'text-amber-400' };
  if (score <= 4) return { score: 75, label: 'Good', bar: 'bg-amber-400', text: 'text-amber-400' };
  return { score: 100, label: 'Strong', bar: 'bg-emerald-300', text: 'text-emerald-300' };
}

// ---------------------------------------------------------------------------
// PasswordResetPage — in the shared AuthShell, like LoginPage. Reads email +
// token from the URL, shows a strength meter while typing, and a
// matched/mismatched hint under confirm. Keeps LoginPage's .rise stagger and
// leave-veil so the auth funnel reads as one experience.
// ---------------------------------------------------------------------------

export default function PasswordResetPage() {
  // v2.2.3 — useSearchParams instead of `window.location.search`. Same
  // architectural fix the AppRouter blank-login bug needed: React-Router
  // soft nav updates its own location context but doesn't re-read
  // window.location, so any stale render after nav would carry old params.
  const [searchParams] = useSearchParams();
  // Read once, then taken out of the address bar (below), as the join and
  // sign-in callback pages do: analytics events on this page (time on page,
  // scrolling) report the address, and it carried the token and the email.
  const [emailParam] = useState(() => searchParams.get('email') ?? '');
  const [tokenParam] = useState(() => searchParams.get('token') ?? '');
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.search) {
      try { window.history.replaceState(window.history.state, '', window.location.pathname); } catch { /* no-op */ }
    }
  }, []);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // What the server says happened: a reset also revokes API keys, and says so.
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Document title parity with Login.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Choose a new password · Cadence';
    return () => { document.title = prev; };
  }, []);

  // Entrance — triple-redundant trigger.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setReady(true); };
    const t1 = window.setTimeout(reveal, 120);
    const t2 = window.setTimeout(reveal, 200);
    window.addEventListener('load', reveal);
    const onVis = () => { if (document.visibilityState === 'visible') reveal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('load', reveal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Leave-veil — soft fade up on successful submit before swapping to the
  // success state, and again when the user clicks through to Sign In.
  const [leaving, setLeaving] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const result = await authApi.resetPassword({
        email: emailParam,
        token: tokenParam,
        newPassword,
      });
      setSuccessMessage(typeof result?.message === 'string' && result.message.trim() ? result.message : null);
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));
      setSuccess(true);
      setLeaving(false);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (Array.isArray(errors)) {
        setError(errors.join(' '));
      } else {
        setError(err.response?.data?.error || 'Password reset failed. The link may have expired.');
      }
      setLeaving(false);
    } finally {
      setLoading(false);
    }
  };

  const goToSignIn = async () => {
    setLeaving(true);
    await new Promise((r) => setTimeout(r, 250));
    navigate('/login');
  };

  return (
    <AuthShell leaving={leaving} width="form">
      <div
        className={cn('rise mb-5', ready && 'is-revealed')}
        style={{ '--d': '120ms' } as React.CSSProperties}
      >
        <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
      </div>

      <h1
        className={cn(
          'rise mb-[10px]',
          AUTH_TITLE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '260ms' } as React.CSSProperties}
      >
        Choose a new password.
      </h1>
      <p
        className={cn(
          'rise mb-[30px]',
          AUTH_SUBTITLE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '380ms' } as React.CSSProperties}
      >
        {emailParam ? (
          <>Setting password for <span className="text-foreground/85">{emailParam}</span>.</>
        ) : (
          <>Pick something strong you can remember.</>
        )}
      </p>

      {error && (
        <Alert variant="error" className="mb-3 rounded-xl">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success ? (
        <div
          className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
          style={{ '--d': '520ms' } as React.CSSProperties}
        >
          <Alert variant="success" className="rounded-xl">
            <AlertDescription>
              {successMessage ?? 'Password reset successfully. You can now sign in with your new password.'}
            </AlertDescription>
          </Alert>
          <Button
            onClick={goToSignIn}
            className={cn('mt-[6px]', AUTH_PRIMARY_BUTTON)}
          >
            Go to sign in
          </Button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
          style={{ '--d': '520ms' } as React.CSSProperties}
        >
          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="new-password" className={AUTH_LABEL_CLASS}>
              New password
            </Label>
            <Input
              id="new-password"
              type="password"
              required
              autoFocus
              minLength={8}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={AUTH_FIELD_CLASS}
            />
            {newPassword && (
              <div className="mt-1 flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-foreground/10">
                  <div
                    className={cn('h-full rounded-full transition-all', strength.bar)}
                    style={{ width: `${strength.score}%` }}
                  />
                </div>
                <p className={cn('min-w-[40px] text-xs tracking-wide', strength.text)}>
                  {strength.label}
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Min 8 chars, uppercase, lowercase, number, special character.
            </p>
          </div>

          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="confirm-password" className={AUTH_LABEL_CLASS}>
              Confirm new password
            </Label>
            <Input
              id="confirm-password"
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={AUTH_FIELD_CLASS}
            />
            {confirmPassword && (
              <p className={cn('text-xs tracking-wide', passwordsMatch ? 'text-emerald-300' : 'text-red-400')}>
                {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword}
            className={cn('mt-[6px]', AUTH_PRIMARY_BUTTON)}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Reset password'}
          </Button>
        </form>
      )}

      <div
        className={cn(
          'rise mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground',
          ready && 'is-revealed',
        )}
        style={{ '--d': '1000ms' } as React.CSSProperties}
      >
        <Link to="/login" className={AUTH_FOOTER_LINK_CLASS}>
          Back to sign in
        </Link>
        <span aria-hidden className="text-foreground/30">·</span>
        <Link to="/signup" className={AUTH_FOOTER_LINK_CLASS}>
          14 day free trial
        </Link>
      </div>

      <p
        className={cn(
          'rise mt-8 text-center',
          AUTH_FOOTNOTE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '1200ms' } as React.CSSProperties}
      >
        Cadence is the product. Stratium is the company behind it.
      </p>
    </AuthShell>
  );
}
